import Reflux from "reflux";
import isArray from "lodash/isArray";
import ApiRoutes from "routing/ApiRoutes";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import { singletonStore, singletonActions } from "logic/singleton";
export const IndexSetsActions = singletonActions(
  "core.IndexSets",
  () => Reflux.createActions({
    list: { asyncResult: true },
    listPaginated: { asyncResult: true },
    get: { asyncResult: true },
    update: { asyncResult: true },
    create: { asyncResult: true },
    delete: { asyncResult: true },
    searchPaginated: { asyncResult: true },
    setDefault: { asyncResult: true },
    stats: { asyncResult: true }
  })
);
export const IndexSetsStore = singletonStore(
  "core.IndexSets",
  () => Reflux.createStore({
    listenables: [IndexSetsActions],
    indexSetsCount: void 0,
    indexSets: void 0,
    indexSetStats: void 0,
    indexSet: void 0,
    globalIndexSetStats: void 0,
    getInitialState() {
      return this.getState();
    },
    getState() {
      return {
        indexSetsCount: this.indexSetsCount,
        indexSets: this.indexSets,
        indexSetStats: this.indexSetStats,
        indexSet: this.indexSet,
        globalIndexSetStats: this.globalIndexSetStats
      };
    },
    propagateChanges() {
      this.trigger(this.getState());
    },
    list(stats) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.list(stats).url);
      const promise = fetch("GET", url);
      promise.then(
        (response) => {
          this.indexSetsCount = response.total;
          this.indexSets = response.index_sets;
          this.indexSetStats = response.stats;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(`Fetching index sets list failed: ${error.message}`, "Could not retrieve index sets.");
        }
      );
      IndexSetsActions.list.promise(promise);
    },
    listPaginated(skip, limit, stats) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.listPaginated(skip, limit, stats).url);
      const promise = fetch("GET", url);
      promise.then(
        (response) => {
          this.indexSetsCount = response.total;
          this.indexSets = response.index_sets;
          this.indexSetStats = response.stats;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Fetching index sets list failed: ${this._errorMessage(error)}`,
            "Could not retrieve index sets."
          );
        }
      );
      IndexSetsActions.listPaginated.promise(promise);
    },
    searchPaginated(searchTerm, skip, limit, stats) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.searchPaginated(searchTerm, skip, limit, stats).url);
      const promise = fetch("GET", url);
      promise.then(
        (response) => {
          this.indexSetsCount = response.total;
          this.indexSets = response.index_sets;
          this.indexSetStats = response.stats;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Fetching index sets list failed: ${this._errorMessage(error)}`,
            "Could not retrieve index sets."
          );
        }
      );
      IndexSetsActions.searchPaginated.promise(promise);
    },
    get(indexSetId) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.get(indexSetId).url);
      const promise = fetch("GET", url);
      promise.then(
        (response) => {
          this.indexSet = response;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Fetching index set '${indexSetId}' failed with status: ${this._errorMessage(error)}`,
            "Could not retrieve index set."
          );
        }
      );
      IndexSetsActions.get.promise(promise);
    },
    update(indexSet) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.get(indexSet.id).url);
      const promise = fetch("PUT", url, indexSet);
      promise.then(
        (response) => {
          UserNotification.success(`Successfully updated index set '${indexSet.title}'`, "Success");
          this.indexSet = response;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Updating index set '${indexSet.title}' failed with status: ${this._errorMessage(error)}`,
            "Could not update index set."
          );
        }
      );
      IndexSetsActions.update.promise(promise);
    },
    create(indexSet) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.create().url);
      const promise = fetch("POST", url, indexSet);
      promise.then(
        (response) => {
          UserNotification.success(`Successfully created index set '${indexSet.title}'`, "Success");
          this.indexSet = response;
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Creating index set '${indexSet.title}' failed with status: ${this._errorMessage(error)}`,
            "Could not create index set."
          );
        }
      );
      IndexSetsActions.create.promise(promise);
    },
    delete(indexSet, deleteIndices) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.delete(indexSet.id, deleteIndices).url);
      const promise = fetch("DELETE", url);
      promise.then(
        () => {
          UserNotification.success(`Successfully deleted index set '${indexSet.title}'`, "Success");
        },
        (error) => {
          UserNotification.error(
            `Deleting index set '${indexSet.title}' failed with status: ${this._errorMessage(error)}`,
            "Could not delete index set."
          );
        }
      );
      IndexSetsActions.delete.promise(promise);
    },
    setDefault(indexSet) {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.setDefault(indexSet.id).url);
      const promise = fetch("PUT", url);
      promise.then(
        () => {
          UserNotification.success(`Successfully set index set '${indexSet.title}' as default`, "Success");
        },
        (error) => {
          UserNotification.error(
            `Setting index set '${indexSet.title}' as default failed with status: ${this._errorMessage(error)}`,
            "Could not set default index set."
          );
        }
      );
      IndexSetsActions.setDefault.promise(promise);
    },
    stats() {
      const url = qualifyUrl(ApiRoutes.IndexSetsApiController.stats().url);
      const promise = fetch("GET", url);
      promise.then(
        (response) => {
          this.globalIndexSetStats = {
            indices: response.indices,
            documents: response.documents,
            size: response.size
          };
          this.propagateChanges();
          return response;
        },
        (error) => {
          UserNotification.error(
            `Fetching global index stats failed: ${error.message}`,
            "Could not retrieve global index stats."
          );
        }
      );
      IndexSetsActions.stats.promise(promise);
    },
    _errorMessage(error) {
      try {
        if (isArray(error.additional.body)) {
          return error.additional.body.map(({ message, path }) => `${path != null ? path : ""} ${message}.`).join(" ");
        }
        return error.additional.body.message;
      } catch (_e) {
        return error.message;
      }
    }
  })
);
